import { createContext, useContext, useState, useEffect } from 'react';
import { products as initialProducts, categories, productInCategory, getProductCategories } from '../data/products';

const ProductContext = createContext();

export const ProductProvider = ({ children }) => {
  const [products] = useState(initialProducts);
  const [categoriesList] = useState(categories);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [priceRange, setPriceRange] = useState([0, 500]);
  const [sortBy, setSortBy] = useState('featured');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredProducts, setFilteredProducts] = useState(initialProducts);

  useEffect(() => {
    let result = [...products];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((product) => {
        const cats = getProductCategories(product).join(' ').toLowerCase();
        return (
          product.name.toLowerCase().includes(query) ||
          product.brand.toLowerCase().includes(query) ||
          cats.includes(query) ||
          (product.subcategory || '').toLowerCase().includes(query)
        );
      });
    }

    if (selectedCategory !== 'all') {
      result = result.filter((product) => productInCategory(product, selectedCategory));
    }

    result = result.filter((product) => {
      const price = product.salePrice || product.price;
      return price >= priceRange[0] && price <= priceRange[1];
    });

    switch (sortBy) {
      case 'price-low':
        result.sort((a, b) => (a.salePrice || a.price) - (b.salePrice || b.price));
        break;
      case 'price-high':
        result.sort((a, b) => (b.salePrice || b.price) - (a.salePrice || a.price));
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      default:
        break;
    }

    setFilteredProducts(result);
  }, [products, selectedCategory, priceRange, sortBy, searchQuery]);

  const getProductById = (id) => products.find((product) => product.id === id);
  const getProductsByCategory = (categoryName) =>
    products.filter((product) => productInCategory(product, categoryName));

  const clearFilters = () => {
    setSelectedCategory('all');
    setPriceRange([0, 500]);
    setSortBy('featured');
    setSearchQuery('');
  };

  return (
    <ProductContext.Provider
      value={{
        products,
        categories: categoriesList,
        filteredProducts,
        selectedCategory,
        setSelectedCategory,
        priceRange,
        setPriceRange,
        sortBy,
        setSortBy,
        searchQuery,
        setSearchQuery,
        getProductById,
        getProductsByCategory,
        clearFilters,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};

export const useProducts = () => {
  const context = useContext(ProductContext);
  if (!context) throw new Error('useProducts must be used within a ProductProvider');
  return context;
};

export default ProductContext;
